/**
 * SubscriptionBanner — In-app subscription status feedback.
 *
 * Displays contextual banners for authenticated users based on their
 * current subscription state:
 *   - Trial ending soon (< 7 days): warning
 *   - Trial expired / no subscription: error
 *   - Payment past due: warning
 *   - Subscription canceled: info
 *
 * Renders nothing for unauthenticated users or healthy subscriptions.
 */

import { Link } from 'react-router-dom';
import { AlertTriangle, XCircle, Info, X } from 'lucide-react';
import { useState } from 'react';
import { useNowMs } from '@/hooks/useNowMs';
import { useAuth } from '@/contexts/AuthContext';
import { useUserInfo } from '@/hooks/useUserInfo';

// ---------------------------------------------------------------------------
// Banner sub-component
// ---------------------------------------------------------------------------

interface BannerAction {
  label: string;
  href: string;
}

interface BannerProps {
  variant: 'warning' | 'error' | 'info';
  message: string;
  action?: BannerAction;
}

const VARIANT_STYLES: Record<BannerProps['variant'], {
  wrapper: string;
  icon: string;
  text: string;
  action: string;
  dismiss: string;
}> = {
  warning: {
    wrapper: 'bg-amber-500/10 border-amber-500/30',
    icon: 'text-amber-400',
    text: 'text-amber-200',
    action: 'bg-amber-500 hover:bg-amber-600 text-white',
    dismiss: 'text-amber-400/70 hover:text-amber-300',
  },
  error: {
    wrapper: 'bg-red-500/10 border-red-500/30',
    icon: 'text-red-400',
    text: 'text-red-200',
    action: 'bg-red-500 hover:bg-red-600 text-white',
    dismiss: 'text-red-400/70 hover:text-red-300',
  },
  info: {
    wrapper: 'bg-blue-500/10 border-blue-500/30',
    icon: 'text-blue-400',
    text: 'text-blue-200',
    action: 'bg-blue-500 hover:bg-blue-600 text-white',
    dismiss: 'text-blue-400/70 hover:text-blue-300',
  },
};

const VARIANT_ICONS: Record<BannerProps['variant'], typeof AlertTriangle> = {
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
};

function Banner({ variant, message, action }: BannerProps) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const styles = VARIANT_STYLES[variant];
  const Icon = VARIANT_ICONS[variant];

  return (
    <div className={`border-b ${styles.wrapper}`}>
      <div className="container mx-auto px-4 py-2.5 flex items-center gap-3">
        <Icon className={`w-4 h-4 flex-shrink-0 ${styles.icon}`} />
        <p className={`flex-1 text-sm ${styles.text}`}>{message}</p>
        {action && (
          <Link
            to={action.href}
            className={`flex-shrink-0 inline-flex items-center rounded-md px-3 py-1 text-xs font-semibold transition-colors ${styles.action}`}
          >
            {action.label}
          </Link>
        )}
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className={`flex-shrink-0 transition-colors ${styles.dismiss}`}
          aria-label="Dismiss banner"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SubscriptionBanner — picks the right banner based on subscription status
// ---------------------------------------------------------------------------

export function SubscriptionBanner() {
  const { authenticated } = useAuth();
  const { data: userInfo } = useUserInfo();
  const nowMs = useNowMs();

  if (!authenticated || !userInfo) return null;

  const sub = userInfo.subscription;
  const status = sub?.status;
  const trialEndsAt = sub?.trialEndsAt;

  // ── Trial ending within 7 days ───────────────────────────────────────────
  if (status === 'trialing' && trialEndsAt) {
    const daysLeft = Math.ceil(
      (new Date(trialEndsAt).getTime() - nowMs) / 86_400_000
    );
    if (daysLeft <= 7 && daysLeft > 0) {
      return (
        <Banner
          variant="warning"
          message={`Your free trial ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'}. Add a payment method to keep your access.`}
          action={{ label: 'Add Payment', href: '/account' }}
        />
      );
    }
  }

  // ── Trial expired / no active subscription ────────────────────────────────
  const trialExpired =
    status === 'trialing' &&
    trialEndsAt &&
    new Date(trialEndsAt).getTime() < nowMs;

  if (
    !sub ||
    status === 'expired' ||
    status === 'incomplete_expired' ||
    trialExpired
  ) {
    return (
      <Banner
        variant="error"
        message="Your trial has expired. Subscribe to continue using the platform."
        action={{ label: 'Subscribe', href: '/pricing' }}
      />
    );
  }

  // ── Past due ─────────────────────────────────────────────────────────────
  if (status === 'past_due') {
    return (
      <Banner
        variant="warning"
        message="Your payment failed. Please update your payment method to avoid service interruption."
        action={{ label: 'Update Payment', href: '/account' }}
      />
    );
  }

  // ── Canceled (still active until period end) ─────────────────────────────
  if (status === 'canceled') {
    const periodEnd = sub.periodEnd ?? sub.currentPeriodEnd;
    const endDate = periodEnd
      ? new Date(periodEnd).toLocaleDateString('en-US', {
          timeZone: 'UTC',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })
      : 'the end of your billing period';
    return (
      <Banner
        variant="info"
        message={`Your subscription has been canceled. You'll have access until ${endDate}.`}
        action={{ label: 'Resubscribe', href: '/pricing' }}
      />
    );
  }

  return null;
}
